import { Lock, ScanSearch } from "lucide-react";
import { Badge } from "@/components/atoms/studio/ui/badge";
import { Button } from "@/components/atoms/studio/ui/button";
import { toneBadgeVariant } from "@/lib/migrations/coverage";
import { portfolioScale, type PortfolioGate } from "@/lib/migrations/portfolio";
import type { PortfolioInventory } from "@/services/migrations/portfolio";

/**
 * The org's automation at scale, and the gated action over it.
 *
 * THIS PANEL IS THE SALES SURFACE, and it is deliberately the part that costs nothing.
 * `GET /inventory` is free and answers for an org that has bought nothing, so everything
 * above the action — the rule count, the object and profile counts, the per-object
 * breakdown — renders in full for every org. What is gated is the RUN, not the picture.
 *
 * THREE RULES ARE STRUCTURAL HERE, NOT STYLISTIC.
 *
 *  1. THE HEADLINE IS THE TOTAL. `counts.workflow_rules`, never `counts.addressable`.
 *     They differ by `unaddressable`, and `addressable` is always the larger-sounding
 *     number of the two. `lib/migrations/portfolio.portfolioScale` pins this so no
 *     component can quietly swap them.
 *
 *  2. `unaddressable` RENDERS BESIDE THE HEADLINE, AT EVERY VALUE. Not in a disclosure,
 *     not only when non-zero — the same rule the engine applies to `undeclared`, and for
 *     the same reason: a number that only appears when it is inconvenient teaches readers
 *     to stop looking for it. At zero it is genuinely good news and says so. Absent, it
 *     says the server did not report it, and never renders as `0`.
 *
 *  3. THE REFUSAL IS NOT AN ERROR. When the pass is not entitled the action is disabled —
 *     `disabled` AND `aria-disabled`, so assistive technology is told the same thing the
 *     pointer is — and beneath it sits the server's own sentence plus `triage.contact`.
 *     No red, no alert role, no "failed". This is a product boundary with a path across
 *     it, and a customer reading it should come away knowing who to call, not thinking
 *     the page broke.
 *
 * The panel is PURE: props in, markup out, no hooks and no queries. That is what lets its
 * tests render the real component and assert on the real attributes rather than on a
 * view-model that a component is free to ignore.
 */
export function PortfolioInventoryPanel({
  inventory,
  gate,
  onRun,
  running = false,
}: {
  inventory: PortfolioInventory;
  /** From `resolvePortfolioGate` — the inventory's triage, or a 403 that outranked it. */
  gate: PortfolioGate;
  onRun?: () => void;
  running?: boolean;
}) {
  const scale = portfolioScale(inventory);

  return (
    <div className="space-y-5">
      {/* ── the measured scale ─────────────────────────────────────────────── */}
      <div className="rounded-xl border border-border bg-card p-5">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="font-mono text-3xl tabular-nums text-foreground">
            {scale.headline.value}
          </span>
          <span className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
            {scale.headline.label} in this org
          </span>
          {/* Beside the headline, always. Never behind a disclosure. */}
          <Badge variant={toneBadgeVariant(scale.unaddressable.tone)}>
            {scale.unaddressable.label}
          </Badge>
        </div>

        <p className="mt-1 max-w-prose text-xs text-muted-foreground">
          {scale.unaddressable.detail}
        </p>

        {scale.caveats.length > 0 ? (
          <ul className="mt-3 space-y-1">
            {scale.caveats.map((caveat) => (
              <li key={caveat} className="max-w-prose text-xs text-[hsl(var(--accent-warm))]">
                {caveat}
              </li>
            ))}
          </ul>
        ) : null}

        <dl className="mt-4 flex flex-wrap gap-x-8 gap-y-2">
          {scale.metrics.map((metric) => (
            <div key={metric.label}>
              <dt className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
                {metric.label}
              </dt>
              <dd className="font-mono text-lg tabular-nums text-foreground">{metric.value}</dd>
            </div>
          ))}
        </dl>
      </div>

      <PortfolioAction gate={gate} onRun={onRun} running={running} />

      <PortfolioByObjectTable inventory={inventory} />
    </div>
  );
}

/**
 * The gated action.
 *
 * The disabled attribute here is a COURTESY, not a control — `/estimate`, `/passes`,
 * `/run` and `/triage` are enforced server-side and refuse a 403 whether or not this
 * button was clickable. What the client owes the reader is the other half: the reason,
 * and the way forward.
 */
function PortfolioAction({
  gate,
  onRun,
  running,
}: {
  gate: PortfolioGate;
  onRun?: () => void;
  running?: boolean;
}) {
  const disabled = !gate.available || running === true;

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          onClick={onRun}
          disabled={disabled}
          // Stated twice on purpose. `disabled` stops the pointer; `aria-disabled` is
          // what a screen reader announces, and a control that is visually unavailable
          // while announcing itself as available is a worse failure than either alone.
          aria-disabled={disabled}
          data-testid="portfolio-run"
        >
          <ScanSearch className="mr-1.5 h-3.5 w-3.5" />
          {running ? "Running portfolio pass…" : "Run portfolio pass"}
        </Button>

        {!gate.available ? (
          <Badge variant="secondary">
            <Lock className="mr-1 h-3 w-3" />
            {/* NOT "paid capability" when the refusal is a flag. Calling a deployment
                state a paid capability tells a customer who has already bought the pass
                that they have not. */}
            {gate.deploymentDisabled ? "not deployed here" : "paid capability"}
          </Badge>
        ) : null}
      </div>

      {gate.available ? (
        <p className="mt-3 max-w-prose text-xs text-muted-foreground">
          The pass compiles every addressable rule, runs a check-only deploy for each, and
          clusters the results. It writes nothing to the org.
        </p>
      ) : (
        // Muted, not destructive. This is a product boundary, and it has a door.
        <div className="mt-3 space-y-1.5 text-xs text-muted-foreground">
          <p className="max-w-prose text-foreground">{gate.message}</p>
          {/* The server's `reason` is a machine token (`no_entitlement_record`), not a
              sentence. Rendered in mono so it reads as the diagnostic it is rather than
              as prose written for the person on the screen — it is worth showing, because
              it is what an account team will ask for. */}
          {gate.reason ? <p className="max-w-prose font-mono text-[11px]">{gate.reason}</p> : null}
          {/* THE CONTACT PATH IS CONDITIONAL, and this is the whole point of the field
              being nullable. `PORTFOLIO_DISABLED` is a deployment state: an operator turns
              a flag on, and there is nobody to call about it. Printing "next step: contact
              your account team" under it would send a customer who may already hold the
              entitlement to go and buy what they have. */}
          {gate.contact ? (
            <p className="max-w-prose">
              <span className="font-semibold text-foreground">Next step:</span> {gate.contact}
            </p>
          ) : (
            <p className="max-w-prose">
              This is a configuration of this environment and not a fact about your org or
              your entitlement. Nothing here needs to be purchased; the capability is not
              switched on where this service is running.
            </p>
          )}
          {gate.fromRefusal ? (
            // Says the click did something. Without this, a button that was somehow
            // enabled and then refused looks like a button that does nothing at all.
            <p className="max-w-prose">
              The request reached the server and was declined
              {gate.deploymentDisabled ? "" : " for this org"}
              {gate.code ? ` (${gate.code})` : ""}. Nothing was started and nothing was
              charged.
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}

/** Rules per object, in the order the server sent them (count-descending). */
function PortfolioByObjectTable({ inventory }: { inventory: PortfolioInventory }) {
  const rows = inventory.by_object.rows;
  if (rows.length === 0) {
    return (
      <p className="font-mono text-xs text-muted-foreground">
        {inventory.by_object.unreadable > 0
          ? "The per-object breakdown could not be read."
          : "No per-object breakdown was returned for this org."}
      </p>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <table className="w-full text-left">
        <caption className="sr-only">Workflow rules per object</caption>
        <thead>
          <tr className="border-b border-border">
            <th className="px-4 py-2 font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
              object
            </th>
            <th className="px-4 py-2 text-right font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
              rules
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.object} className="border-b border-border last:border-0">
              <td className="px-4 py-1.5 font-mono text-xs text-foreground">{row.object}</td>
              <td className="px-4 py-1.5 text-right font-mono text-xs tabular-nums text-foreground">
                {row.rules}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The org could not be read (`502 org_unreadable`).
 *
 * A DISTINCT STATE, and deliberately not the gate. Nothing about it is about money: the
 * pass may well be entitled, and showing "contact your account team" here would send a
 * customer to a salesperson for what is a connection problem. Destructive tone, because
 * unlike the refusal this genuinely is a failure.
 */
export function PortfolioOrgUnreadable({ message }: { message: string }) {
  return (
    <div className="rounded-xl border border-destructive/40 bg-card p-5">
      <p className="font-mono text-sm text-destructive">This org could not be read.</p>
      <p className="mt-1 max-w-prose text-xs text-muted-foreground">
        The inventory reached Salesforce but could not list this org's automation, so there
        is no scale to show and no count on this page would be true. This is not an
        entitlement problem — nothing here is gated behind a purchase.
      </p>
      {message ? (
        <p className="mt-2 max-w-prose font-mono text-xs text-muted-foreground">{message}</p>
      ) : null}
    </div>
  );
}

/** No portfolio API in this deployment. Mirrors `EngineUnavailable`: said once, plainly. */
export function PortfolioUnavailable() {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <p className="font-mono text-sm text-foreground">
        The portfolio inventory is not available.
      </p>
      <p className="mt-1 max-w-prose text-xs text-muted-foreground">
        This environment has no portfolio API. Nothing is broken and no data is missing —
        the surface simply has nothing to talk to.
      </p>
    </div>
  );
}
